import hexRgb from "hex-rgb";
import moment from "moment";

export const _hexToRGBA = (rgb: string, alpha: number) => {
  const rsRGB = hexRgb(rgb);
  return { r: rsRGB.red, g: rsRGB.green, b: rsRGB.blue, a: alpha };
};

export const toPixels = (length: number) => {
  let conversionFactor = 96;
  conversionFactor /= 25.4;

  return conversionFactor * length + "px";
};

export const pixelToMm = (pixels: number, dpi: number) =>
  Number((pixels * 25.4) / dpi);

export const mmToPixel = (mm: number, dpi: number) => Number((dpi * mm) / 25.4);

export const zoomLevelToScaleText = (zoomLevel: number) => {
  switch (true) {
    case zoomLevel === 22:
      return 75;
    case zoomLevel < 22 && zoomLevel >= 21:
      return Number((150 - 75) * (22 - zoomLevel)) + 75;
    case zoomLevel < 21 && zoomLevel >= 20:
      return Number((300 - 150) * (21 - zoomLevel)) + 150;
    case zoomLevel < 20 && zoomLevel >= 19:
      return Number((600 - 300) * (20 - zoomLevel)) + 300;
    case zoomLevel < 19 && zoomLevel >= 18:
      return Number((1250 - 600) * (19 - zoomLevel)) + 600;
    case zoomLevel < 18 && zoomLevel >= 17:
      return Number((2500 - 1250) * (18 - zoomLevel)) + 1250;
    case zoomLevel < 17 && zoomLevel >= 16:
      return Number((5000 - 2500) * (17 - zoomLevel)) + 2500;
    case zoomLevel < 16 && zoomLevel >= 15:
      return Number((10000 - 5000) * (16 - zoomLevel)) + 5000;
    case zoomLevel < 15 && zoomLevel >= 14:
      return Number((20000 - 10000) * (15 - zoomLevel)) + 10000;
    case zoomLevel < 14 && zoomLevel >= 13:
      return Number((40000 - 20000) * (14 - zoomLevel)) + 20000;
    case zoomLevel < 13 && zoomLevel >= 12:
      return Number((80000 - 40000) * (13 - zoomLevel)) + 40000;
    case zoomLevel < 12 && zoomLevel >= 11:
      return Number((160000 - 80000) * (12 - zoomLevel)) + 80000;
    case zoomLevel < 11 && zoomLevel >= 10:
      return Number((320000 - 160000) * (11 - zoomLevel)) + 160000;
    case zoomLevel < 10 && zoomLevel >= 9:
      return Number((640000 - 320000) * (10 - zoomLevel)) + 320000;
    case zoomLevel < 9 && zoomLevel >= 8:
      return Number((1250000 - 640000) * (9 - zoomLevel)) + 640000;
    case zoomLevel < 8 && zoomLevel >= 7:
      return Number((2500000 - 1250000) * (8 - zoomLevel)) + 1250000;
    case zoomLevel < 7 && zoomLevel >= 6:
      return Number((5000000 - 2500000) * (7 - zoomLevel)) + 2500000;
    case zoomLevel < 6 && zoomLevel >= 5:
      return Number((10000000 - 5000000) * (6 - zoomLevel)) + 5000000;
    case zoomLevel < 5 && zoomLevel >= 4:
      return Number((20000000 - 10000000) * (5 - zoomLevel)) + 10000000;
    case zoomLevel < 4 && zoomLevel >= 3:
      return Number((40000000 - 20000000) * (4 - zoomLevel)) + 20000000;
    default:
      return 320000000;
  }
};

export const scaleTextToZoomLevel = (scaleText: number) => {
  switch (true) {
    case scaleText === 75:
      return 22;
    case scaleText > 75 && scaleText <= 150:
      return 22 - (scaleText - 75) / (150 - 75);
    case scaleText > 150 && scaleText <= 300:
      return 21 - (scaleText - 150) / (300 - 150);
    case scaleText > 300 && scaleText <= 600:
      return 20 - (scaleText - 300) / (600 - 300);
    case scaleText > 600 && scaleText <= 1250:
      return 19 - (scaleText - 600) / (1250 - 600);
    case scaleText > 1250 && scaleText <= 2500:
      return 18 - (scaleText - 1250) / (2500 - 1250);
    case scaleText > 2500 && scaleText <= 5000:
      return 17 - (scaleText - 2500) / (5000 - 2500);
    case scaleText > 5000 && scaleText <= 10000:
      return 16 - (scaleText - 5000) / (10000 - 5000);
    case scaleText > 10000 && scaleText <= 20000:
      return 15 - (scaleText - 10000) / (20000 - 10000);
    case scaleText > 20000 && scaleText <= 40000:
      return 14 - (scaleText - 20000) / (40000 - 20000);
    case scaleText > 40000 && scaleText <= 80000:
      return 13 - (scaleText - 40000) / (80000 - 40000);
    case scaleText > 80000 && scaleText <= 160000:
      return 12 - (scaleText - 80000) / (160000 - 80000);
    case scaleText > 160000 && scaleText <= 320000:
      return 11 - (scaleText - 160000) / (320000 - 160000);
    case scaleText > 320000 && scaleText <= 640000:
      return 10 - (scaleText - 320000) / (640000 - 320000);
    case scaleText > 640000 && scaleText <= 1250000:
      return 9 - (scaleText - 640000) / (1250000 - 640000);
    case scaleText > 1250000 && scaleText <= 2500000:
      return 8 - (scaleText - 640000) / (1250000 - 640000);
    case scaleText > 2500000 && scaleText <= 5000000:
      return 7 - (scaleText - 1250000) / (5000000 - 1250000);
    case scaleText > 5000000 && scaleText <= 10000000:
      return 6 - (scaleText - 5000000) / (10000000 - 5000000);
    case scaleText > 10000000 && scaleText <= 20000000:
      return 5 - (scaleText - 10000000) / (20000000 - 10000000);
    case scaleText > 20000000 && scaleText <= 40000000:
      return 4 - (scaleText - 20000000) / (40000000 - 20000000);
    default:
      return 3;
  }
};

export const paperList = [
  {
    id: "a3",
    label: "A3",
    landscape: {
      originX: 20,
      originY: 20,
      width: 380,
      height: 190,
      desTableX: 230,
      desTableY: 215,
      northArrowX: 140,
      northArrowY: 215,
      scaleBarX: 125,
      scaleBarY: 260,
      overviewWidth: 65,
      overviewHeight: 50,
      overviewX: 20,
      overviewY: 215,
    },
    portrait: {
      originX: 20,
      originY: 20,
      width: 260,
      height: 290,
      desTableX: 110,
      desTableY: 335,
      northArrowX: 85,
      northArrowY: 315,
      scaleBarX: 210,
      scaleBarY: 315,
      overviewWidth: 60,
      overviewHeight: 60,
      overviewX: 20,
      overviewY: 315,
    },
  },
  {
    id: "a4",
    label: "A4",
    landscape: {
      originX: 5,
      originY: 5,
      width: 287,
      height: 130,
      desTableX: 122,
      desTableY: 140,
      northArrowX: 80,
      northArrowY: 140,
      scaleBarX: 62,
      scaleBarY: 185,
      overviewWidth: 50,
      overviewHeight: 50,
      overviewX: 5,
      overviewY: 140,
    },
    portrait: {
      originX: 5,
      originY: 5,
      width: 200,
      height: 220,
      desTableX: 35,
      desTableY: 228,
      northArrowX: 15,
      northArrowY: 265,
      scaleBarX: 8,
      scaleBarY: 211,
      overviewWidth: 28,
      overviewHeight: 28,
      overviewX: 5,
      overviewY: 228,
    },
  },
];

export const scaleList = [
  {
    scale: "1:100",
    value: 100,
    zoom: 21.6666666667,
  },
  {
    scale: "1:250",
    value: 250,
    zoom: 20.333333333,
  },
  {
    scale: "1:500",
    value: 500,
    zoom: 19.333333333,
  },
  {
    scale: "1:1,000",
    value: 1000,
    zoom: 18.384615384615,
  },
];

export const scaleBars: any = {
  "18.384615384615": {
    widthPerGrid: (dpi: number) => mmToPixel(5, dpi),
    meters: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50],
  },
  "19.333333333": {
    widthPerGrid: (dpi: number) => mmToPixel(10, dpi),
    meters: [0, 5, 10, 15, 20, 25],
  },
  "20.333333333": {
    widthPerGrid: (dpi: number) => mmToPixel(10, dpi),
    meters: [0, 2.5, 5, 7.5, 10, 12.5, 15],
  },
  "21.6666666667": {
    widthPerGrid: (dpi: number) => mmToPixel(10, dpi),
    meters: [0, 1, 2, 3, 4, 5],
  },
};

export const textSizeList = [6, 7, 8, 9, 10, 11, 12, 14, 18, 24, 48, 72];

export const addImageProcess: ImageData | any = (
  src: string,
  width: number,
  height: number
) => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.width = width;
    img.height = height;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

export const calCadSize = async (svgString: string) => {
  const parser = new DOMParser();
  const svgElement: SVGSVGElement | null = parser
    .parseFromString(svgString, "text/xml")
    .querySelector("svg");
  const rootViewBox = svgElement?.getAttribute("viewBox");
  const arrayViewBox: any = rootViewBox?.split(" ");
  const svgWidth = Math.abs(Number(arrayViewBox[0]) - Number(arrayViewBox[2]));
  const svgHeigth = Math.abs(Number(arrayViewBox[1]) - Number(arrayViewBox[3]));

  return { width: svgWidth, height: svgHeigth };
};

export const sortKeyDate = (arr: any, key: string, type: "asc" | "desc") => {
  return arr
    ? arr.sort(function (a: any, b: any) {
        var keyA = moment(a[key]),
          keyB = moment(b[key]);
        // Compare the 2 dates
        if (keyA < keyB) return type === "asc" ? -1 : 1;
        if (keyA > keyB) return type === "asc" ? 1 : -1;
        return 0;
      })
    : [];
};
